/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import { useEffect, useRef } from 'react';

/**
 * Enter ממשיך לשלב הבא - בכל מסך בזרימה.
 *
 * ## למה זה נדרש בכלל
 *
 * הדפדפן שולח טופס ב-Enter מעצמו, אבל רק בתנאים שכמעט אף מסך כאן אינו עומד בהם:
 * צריך `<form>`, וצריך בתוכו כפתור שליחה **או** שדה טקסט יחיד. המסכים כאן מציירים
 * את הפעולה הראשית כ-`<div className='button' onClick=...>` - כלומר אין כפתור
 * שליחה - וחלקם אינם עטופים ב-`<form>` כלל. התוצאה היא שב-`/register`, שיש בו חמישה
 * שדות, Enter לא עשה דבר, ובמסכי הקוד הוא לא עשה דבר גם כשהשדה היחיד היה מלא.
 *
 * ## למה מאזין ולא תיקון של הטפסים
 *
 * החלופה היא להפוך כל פעולה ראשית ל-`<button type="submit">` בתוך `<form>`. זה נכון
 * יותר, אבל הוא נוגע בעשרה מסכים ובעיצוב של כל כפתור בהם - ו-`button` נושא ברירות
 * מחדל של דפדפן ש-`div` אינו נושא. מאזין אחד נותן את אותה התנהגות בלי לגעת בשום
 * פריסה, והוא גם מה שמאפשר לומר "בכל מקום": הוא אינו תלוי במיקוד בתוך טופס.
 *
 * ## מה הוא אינו תופס, ולמה
 *
 * מסך שכבר עובד נייטיב **אינו** משתמש בהוק הזה - `QuickLogin`, `StepUpDialog`
 * והפריסה הסלולרית של ההתחברות נושאים `type="submit"` אמיתי, ושם הוק נוסף היה יורה
 * את הפעולה פעמיים.
 *
 * @param {Function} onSubmit הפעולה הראשית של המסך.
 * @param {boolean}  enabled  כבוי בזמן שהפעולה בעבודה, או כשאין מה לשלוח.
 */
export function useEnterSubmit(onSubmit, enabled = true) {
    /**
     * הפעולה נקראת דרך `ref`, ולכן המאזין נרשם פעם אחת ואינו נרשם מחדש בכל הקלדה.
     * בלי זה כל תו שמשנה `state` היה מסיר ומוסיף מאזין, וההוק היה הופך את עצמו
     * לעבודה שמתבצעת על כל לחיצת מקש.
     */
    const handler = useRef(onSubmit);
    handler.current = onSubmit;

    useEffect(() => {
        if (!enabled) return undefined;

        const onKeyDown = (e) => {
            if (e.key !== 'Enter') return;
            // צירוף מקשים אינו "המשך" - וגם לא Enter שמאשר הרכבת תו בשיטת קלט אסייתית.
            if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey || e.isComposing || e.keyCode === 229) return;

            const target = e.target;
            const tag = target?.tagName;
            /**
             * אלמנטים שמפעילים את עצמם ב-Enter מטפלים בעצמם.
             *
             * בלי זה Enter על כפתור היה מפעיל גם אותו וגם את הפעולה הראשית - למשל
             * "שליחת קוד מחדש" שהיה שולח קוד **ומיד** מנסה לאמת אותו.
             */
            if (tag === 'BUTTON' || tag === 'A' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (target?.isContentEditable) return;

            /**
             * חלון פתוח חוסם - הוא המסך הפעיל, ולא מה שמתחתיו.
             *
             * דיאלוג השגיאה של היישום ושער האימות (`StepUpDialog`) מצוירים מעל כל
             * מסך, ולשניהם יש פעולה משלהם. Enter שהיה עובר דרכם היה מפעיל את המסך
             * שהם מכסים - כלומר שולח טופס שהמשתמש אינו רואה.
             */
            if (document.querySelector('[role="dialog"]')) return;

            if (typeof handler.current !== 'function') return;

            // הטופס לא יישלח פעמיים: `preventDefault` מבטל גם שליחה נייטיב, אם ישנה.
            e.preventDefault();
            handler.current(e);
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [enabled]);
}

export default useEnterSubmit;
